import { apiErrorMessage, box, container, loadingButton, message, namedLink, pageSubtitle } from '@/ui/components';
import { h, defineComponent, ref, computed } from 'vue';
import { inputField } from '@/ui/components/form';
import { loginSso } from '@/ui/auth/auth';
import { locales, t } from '@/ui/i18n/i18n';
import { useSetLocale, useTask, useValidation } from '@/ui/concerns';
import { LoggingInSsoUser, validate } from '@/entities/auth/loggingInSsoUser';
import { StatusCodes } from 'http-status-codes';
import { AuthIllustration } from '@/ui/components/auth/AuthIllustrations';
import { ReCaptcha } from '@/ui/components/auth/ReCaptcha';
import { v4 as uuidv4 } from 'uuid';
import { loginSsoUrl } from '@/services/api/auth';
import { normalizePath } from '@/services/api';
import * as qs from '@/ui/queryState';

interface LocalePageProps {
  locale?: locales;
}

export const SsoLoginPage = defineComponent<LocalePageProps>({
  name: 'SsoLoginPage',
  props: ['locale'],
  setup(props) {
    useSetLocale(props.locale);

    const user = ref<LoggingInSsoUser>({
      email: '',
    });

    const errorCounter = ref<number>(0);

    // show google recaptcha after three failed submission
    const showReCaptcha = computed(() => {
      return process.env.VUE_APP_GOOGLE_RECAPTCHA_SITE_KEY
        && errorCounter.value > 2;
    });

    const loginInProgress = ref<boolean>(false);
    const isReCaptchaVerified = ref<boolean>(false);
    const showQueryStringError = ref<boolean>(false);
    const queryStringError = t(qs.string('error', '', false).value);
    if (queryStringError.length) {
      showQueryStringError.value = true;
    }

    const {
      run: submitForm,
      state,
    } = useTask(
      () => loginSso(user.value),
      () => {
        window.location.href = normalizePath(loginSsoUrl(user.value));
      },
      uuidv4(),
      error => {
        if (error.code === StatusCodes.UNAUTHORIZED || error.code === StatusCodes.UNPROCESSABLE_ENTITY) {
          errorCounter.value++;
        }
        loginInProgress.value = false;
      },
    );

    const {
      validationErrors,
      hasNoValidationErrors,
      showValidationErrors,
    } = useValidation(validate, user, state);

    const canSubmitForm = () => {
      return hasNoValidationErrors.value && (isReCaptchaVerified.value || !showReCaptcha.value);
    };

    const handleSignIn = () => {
      showValidationErrors.value = true;
      showQueryStringError.value = false;
      if (canSubmitForm()) {
        loginInProgress.value = true;
        submitForm();
        isReCaptchaVerified.value = false;
      }
    };

    return function render() {
      const fields = [
        inputField({
          name: 'email',
          type: 'email',
          label: t('common.email'),
          placeholder: t('auth.enter-email'),
          value: user.value.email,
          required: false,
          errors: validationErrors.value,
          disabled: state.value.pending,
        }, v => {
          user.value.email = v;
        }),
      ];

      return container(
        [
          box([h('form', {
            on: {
              keyup: (e: KeyboardEvent) => e.key === 'Enter' && handleSignIn(),
            },
          }, [
            pageSubtitle(t('auth.sign-in-account-sso')),
            ...fields,
            h(ReCaptcha, {
              props: {
                show: showReCaptcha.value && !state.value.pending,
              },
              on: {
                input: (value: boolean) => {
                  isReCaptchaVerified.value = value;
                },
              },
            }),
            apiErrorMessage(
              state.value,
              'error' in state.value
                ? {
                  [StatusCodes.BAD_REQUEST]: t(state.value.error.message),
                  [StatusCodes.INTERNAL_SERVER_ERROR]: t(state.value.error.message),
                }
                : {},
            ),
            showQueryStringError.value && message('error', queryStringError),
            h(
              'div',
              { class: 'field' },
              [loadingButton(t('auth.sign-in'), handleSignIn, state.value.pending || loginInProgress.value, { expanded: true })],
            ),
          ])]),
          h('div', { class: 'has-text-centered mb-2' }, [
            namedLink(t('auth.sign-in-without-sso'), 'login'),
          ]),
          h(AuthIllustration),
        ],
        'small',
        'px-8 pb-8 pt-10',
      );
    };
  },
});
